import { Package, CheckCircle2, XCircle, Truck, RotateCcw, Banknote, Wallet, Clock, Ban } from 'lucide-react';
import { KpiCard } from './KpiCard';
import { ChartCard } from './ChartCard';
import { ShipmentTrendChart } from '../charts/ShipmentTrendChart';
import { StatusDonutChart } from '../charts/StatusDonutChart';
import { TopCitiesChart } from '../charts/TopCitiesChart';
import { FinancialSummaryCard } from '@/components/features/financials/shared/FinancialSummaryCard';
import {
  useDashboardKpis,
  useShipmentTrend,
  useStatusDistribution,
  useTopCities,
  useRecentActivity,
  useTenantFinance,
} from '@/hooks/useDashboardAnalytics';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate } from '@/lib/formatters';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

const STATUS_LABELS: Record<string, string> = {
  delivered: 'تم التوصيل', in_transit: 'قيد النقل', assigned: 'مُعيَّن',
  picked_up: 'تم الاستلام', pending: 'قيد الانتظار', failed: 'فشل', cancelled: 'ملغى',
};

function formatDateStr(d: string) {
  try { return format(new Date(d), 'd MMM', { locale: ar }); } catch { return d; }
}

function SuccessRateChart({ data }: { data: any[] }) {
  if (!data.length) return (
    <div className="flex items-center justify-center h-[220px] text-muted-foreground text-sm">لا توجد بيانات</div>
  );

  const chartData = data.map((d) => {
    const delivered = d.delivered ?? 0;
    const failed = d.failed ?? 0;
    const total = delivered + failed;
    return {
      date: d.date,
      rate: total > 0 ? Math.round((delivered / total) * 100) : 0,
    };
  });

  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={chartData} margin={{ top: 4, right: 12, left: -16, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" />
        <XAxis dataKey="date" tickFormatter={formatDateStr} tick={{ fontSize: 11 }} />
        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
        <Tooltip
          contentStyle={{ background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }}
          formatter={(v: any) => [`${v}%`, 'نسبة التوصيل']}
          labelFormatter={formatDateStr}
        />
        <Line type="monotone" dataKey="rate" stroke="hsl(142, 72%, 42%)" strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function TenantDashboard() {
  const { data: kpis, loading: kpiLoading } = useDashboardKpis('today');
  const { data: trend, loading: trendLoading } = useShipmentTrend(30);
  const { data: statusDist, loading: statusLoading } = useStatusDistribution();
  const { data: cities, loading: citiesLoading } = useTopCities(8, 'to');
  const { data: activity, loading: activityLoading } = useRecentActivity(10);
  const { data: finance, loading: financeLoading } = useTenantFinance();

  // Calculate this-week KPIs from status distribution
  const delivered = statusDist.find((s: any) => s.status === 'delivered')?.count ?? 0;
  const failed = statusDist.find((s: any) => s.status === 'failed')?.count ?? 0;
  const pending = statusDist.find((s: any) => s.status === 'pending')?.count ?? 0;
  // Returns = shipments being returned (returning) + fully returned (returned)
  const returnsCount = statusDist
    .filter((s: any) => s.status === 'returning' || s.status === 'returned')
    .reduce((sum: number, s: any) => sum + s.count, 0);

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid gap-3 md:gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
        {kpiLoading ? (
          Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)
        ) : (
          <>
            <KpiCard
              title="إجمالي الشحنات"
              value={statusDist.reduce((s: number, d: any) => s + d.count, 0)}
              icon={Package}
              gradient="from-blue-500 to-blue-600"
            />
            <KpiCard
              title="قيد الانتظار"
              value={pending}
              icon={Clock}
              gradient="from-slate-500 to-slate-600"
            />
            <KpiCard
              title="قيد التوصيل"
              value={kpis?.active_deliveries?.value ?? 0}
              icon={Truck}
              gradient="from-orange-500 to-orange-600"
            />
            <KpiCard
              title="تم التوصيل"
              value={delivered}
              icon={CheckCircle2}
              gradient="from-green-500 to-green-600"
            />
            <KpiCard
              title="لم يُوصَّل"
              value={failed}
              icon={XCircle}
              gradient="from-red-500 to-red-600"
            />
            <KpiCard
              title="المرتجعات"
              value={returnsCount}
              icon={RotateCcw}
              gradient="from-purple-500 to-purple-600"
            />
          </>
        )}
      </div>

      {/* Finance row — settlement-centric financial summary */}
      {financeLoading ? (
        <Skeleton className="h-32 rounded-xl" />
      ) : (
        <FinancialSummaryCard
          title="الملخص المالي"
          columns={4}
          metrics={[
            {
              label: 'إجمالي الشحنات (شامل رسوم التوصيل)',
              value: finance?.total_with_fees ?? 0,
              icon: <Banknote className="h-4 w-4" />,
              highlight: 'info',
            },
            {
              label: 'الأرباح المستلمة — شحنات مُسوّاة',
              value: finance?.received_settled ?? 0,
              icon: <Wallet className="h-4 w-4" />,
              highlight: 'success',
            },
            {
              label: 'شحنات لم تتم تسويتها',
              value: finance?.pending_unsettled ?? 0,
              icon: <Clock className="h-4 w-4" />,
              highlight: 'warning',
            },
            {
              label: 'شحنات ملغاة أو غير مسلّمة',
              value: finance?.cancelled_failed ?? 0,
              icon: <Ban className="h-4 w-4" />,
              highlight: 'danger',
            },
          ]}
        />
      )}

      {/* Row 1: Trend + Donut */}
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
        <ChartCard title="شحناتي — آخر 30 يوم" loading={trendLoading} className="lg:col-span-2">
          <ShipmentTrendChart data={trend} days={30} />
        </ChartCard>
        <ChartCard title="توزيع الحالات" loading={statusLoading}>
          <StatusDonutChart data={statusDist} />
        </ChartCard>
      </div>

      {/* Row 2: Success Rate + Top Destinations */}
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
        <ChartCard title="نسبة نجاح التوصيل — آخر 30 يوم" loading={trendLoading}>
          <SuccessRateChart data={trend} />
        </ChartCard>
        <ChartCard title="أكثر مدن التوصيل" loading={citiesLoading}>
          <TopCitiesChart data={cities} />
        </ChartCard>
      </div>

      {/* Recent Shipments */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-foreground/80">آخر التحديثات</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {activityLoading ? (
            <Skeleton className="h-48 w-full rounded-lg" />
          ) : activity.length === 0 ? (
            <div className="flex items-center justify-center h-24 text-muted-foreground text-sm">لا توجد تحديثات</div>
          ) : (
            <div className="space-y-1">
              {activity.map((item: any) => (
                <div key={item.id} className="flex items-center justify-between gap-2 py-2 border-b border-border/40 last:border-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant={item.status_code === 'delivered' ? 'default' : item.status_code === 'failed' ? 'destructive' : 'secondary'} className="text-[10px] shrink-0">
                      {STATUS_LABELS[item.status_code] ?? item.status_code}
                    </Badge>
                    <span className="text-xs font-medium truncate">{item.tracking_number}</span>
                  </div>
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                    {formatDate(new Date(item.created_at), 'dd/MM HH:mm')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
